import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { SERVICE_LINKS, COUNTRY_LINKS, SITE } from "@/lib/content";
import { useAuth } from "@/lib/auth";

export const Wordmark = ({ className = "", dark = false }) => (
  <span className={`font-serif leading-none inline-flex items-baseline ${className}`} style={{ color: dark ? "var(--bone)" : "var(--ink)" }}>
    Prox<span style={{ color: "var(--clay)" }}>.</span>
  </span>
);

const NAV = [
  { label: "Services", to: "/services", drop: SERVICE_LINKS.map((s) => ({ label: s.title, to: `/services/${s.slug}` })) },
  { label: "Pricing", to: "/pricing" },
  { label: "Case studies", to: "/case-studies" },
  { label: "Sourcing", to: "/sourcing", drop: COUNTRY_LINKS.map((c) => ({ label: c.name, to: `/sourcing/${c.slug}` })) },
  { label: "Insights", to: "/insights" },
  { label: "Tools", to: "/tools", drop: [
    { label: "Landed cost & margin calculator", to: "/tools/landed-cost-and-margin-calculator" },
    { label: "Free downloads", to: "/tools/downloads" },
  ] },
  { label: "About", to: "/about" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const { user } = useAuth();

  return (
    <>
      <div className="hidden md:block" style={{ backgroundColor: "var(--sand)" }} data-testid="utility-bar">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-9 flex items-center justify-between font-mono text-[11.5px] uppercase tracking-wide text-[var(--muted)]">
          <span>Melbourne, Australia. {SITE.qualifier}.</span>
          <div className="flex items-center gap-6">
            <a href={`mailto:${SITE.email}`} className="hover:text-[var(--clay-deep)] transition-colors">{SITE.email}</a>
            <Link to="/book" className="hover:text-[var(--clay-deep)] transition-colors">Book a fit call</Link>
            {user ? (
              <Link to="/account" className="hover:text-[var(--clay-deep)] transition-colors" data-testid="nav-account">Account</Link>
            ) : (
              <Link to="/login" className="hover:text-[var(--clay-deep)] transition-colors" data-testid="nav-login">Log in</Link>
            )}
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-50 border-b border-[var(--stone)]" style={{ backgroundColor: "var(--bone)" }} data-testid="site-nav">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-[68px] flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="text-[1.9rem]">
            <Wordmark />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((l) => (
              <div key={l.label} className="nav-group relative">
                <Link
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-1 text-[14px] text-[var(--ink)] hover:text-[var(--clay-deep)] transition-colors py-6"
                >
                  {l.label}
                  {l.drop && <ChevronDown size={13} className="text-[var(--muted)]" />}
                </Link>
                {l.drop && (
                  <div className="nav-drop absolute left-0 top-full w-64 bg-[var(--paper)] border border-[var(--stone)] p-2" style={{ borderRadius: 2 }}>
                    {l.drop.map((d) => (
                      <Link key={d.to} to={d.to} className="block px-3 py-2.5 text-[14px] text-[var(--ink)] hover:bg-[var(--bone)] transition-colors" style={{ borderRadius: 2 }}>
                        {d.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link to="/book" data-testid="nav-cta" className="btn btn-clay text-[14px]" style={{ padding: "11px 22px" }}>
              Book the fit call
            </Link>
          </div>

          <button className="lg:hidden text-[var(--ink)]" data-testid="nav-menu-toggle" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[var(--stone)] max-h-[80vh] overflow-y-auto" data-testid="mobile-menu" style={{ backgroundColor: "var(--bone)" }}>
            <div className="px-6 py-4">
              {NAV.map((l) => (
                <div key={l.label} className="border-b border-[var(--stone)]/70">
                  <div className="flex items-center justify-between">
                    <Link to={l.to} onClick={() => setOpen(false)} className="py-4 text-lg font-serif" data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      {l.label}
                    </Link>
                    {l.drop && (
                      <button aria-label={`Expand ${l.label}`} onClick={() => setExpanded(expanded === l.label ? null : l.label)} className="p-3 acc-sign">
                        {expanded === l.label ? "\u2013" : "+"}
                      </button>
                    )}
                  </div>
                  {l.drop && expanded === l.label && (
                    <div className="pb-3 pl-3">
                      {l.drop.map((d) => (
                        <Link key={d.to} to={d.to} onClick={() => setOpen(false)} className="block py-2.5 text-[15px] text-[var(--muted)]">
                          {d.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link to="/book" onClick={() => setOpen(false)} className="btn btn-clay w-full justify-center mt-5">
                Book the fit call
              </Link>
              <Link to={user ? "/account" : "/login"} onClick={() => setOpen(false)} className="btn btn-outline w-full justify-center mt-3" data-testid="mobile-auth-link">
                {user ? "Account" : "Log in"}
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
